/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Dialog, DialogContent, Grid, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { find, first, isEmpty, isFunction } from 'lodash';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { upsertLineItemTags } from 'store/thunks/tagThunks';
import {
  applyUrgentTag,
  filterPrescriptionsNeedsReview,
  loadPrescriptionData,
  removeUrgentTag,
} from 'store/thunks/workQueueThunks';
import { workQueueActions } from 'store/slices/workQueueSlice';
import { PrescriptionReviewStatus } from 'enums/prescription.enum';
import WorkQueues from 'enums/workQueue.enum';
import useInfoDialog from 'common/hooks/useInfoDialog';
import PatientPrescriptionDialog from 'modules/PatientView/components/PrescriptionsTab/PatientPrescriptionDialog';
import PrescriptionReviewPayloadProvider from 'common/hooks/usePrescriptionReviewPayloadContext';
import ManageTagsDialog from 'modules/PatientView/components/EligibilityActionItems/ManageTagsDialog';

import ResolveDuplicatePrescriptionModal from './ResolveDuplicatePrescriptionModal';
import PrescriptionComparisonModel from './PrescriptionComparisonModel';
import MultiRxReviewDialog from './MultiRxReviewDialog';
import ChangeReviewReasonDialog from './changeReasonButton';

const PrescriptionReviewOptions = ({
  status,
  prescriptionId,
  patientId,
  firstName,
  lastName,
  dob,
  email,
  gender,
  orderId,
  lineItem,
  drugName,
  strength,
  fillType,
  tags,
  patientName,
  isManualReview,
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [resolveModal, setResolveModal] = useState(false);
  const [comparisonView, setComparisonView] = useState(false);
  const [openChangeReasonDialog, setOpenChangeReasonDialog] = useState(false);
  const [isTagsDialogOpen, setIsTagsDialogOpen] = useState(false);
  const menuOpen = !!anchorEl;
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const isDuplicationReview = status === PrescriptionReviewStatus.DUPLICATE_REVIEW;
  const isMultiRxReview = status === PrescriptionReviewStatus.MULTI_RX_REVIEW;
  const isRefillOrder = fillType === 'REFILL';

  const { pages, currentPage, filter } = useSelector(
    ({ workQueue }) => workQueue.prescriptionReview
  );
  const prescriptions = pages[currentPage];
  const prescription = find(
    prescriptions,
    ({ pomPrescriptionId }) => pomPrescriptionId === prescriptionId
  );

  const drugProfile = prescription?.prescriptionData?.prescriptionOverride?.drugProfile;
  const productDetails = prescription?.prescriptionData?.prescriptionOverride?.productDetails;
  const sigProfile = prescription?.prescriptionData?.prescriptionOverride?.sig;
  const form = prescription?.prescriptionData?.form;
  const drugUnit = prescription?.prescriptionData?.drugUnit;
  // Drug identity lives under drugProfile for the legacy flow, or productDetails for
  // productId-flow orders (POM-5715) — either one confirms the drug is resolved.
  const hasDrugIdentity =
    (!isEmpty(drugProfile) && !isFunction(drugProfile)) ||
    (!isEmpty(productDetails) && !isFunction(productDetails));

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const openResolveModal = () => {
    dispatch(
      loadPrescriptionData({
        lineItemId: lineItem,
        prescriptionId,
        reviewType: status,
      })
    );
    setResolveModal(true);
    handleMenuClose();
  };

  const closeResolveModal = (payload) => {
    if (!isEmpty(payload) && !isMultiRxReview) {
      showInfoDialog({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Closing will discard them. Continue?',
        confirmButtonText: 'Yes',
        cancelable: true,
        callback: () => setResolveModal(false),
      });
    } else {
      setResolveModal(false);
    }
  };

  const handleComparisonModalView = () => {
    setResolveModal(!resolveModal);
    dispatch(
      loadPrescriptionData({
        lineItemId: lineItem,
        prescriptionId,
        reviewType: status,
      })
    );
    setComparisonView(!comparisonView);
  };

  const handleResolveDuplicateModal = () => setResolveModal(false);

  const handleManageTagsClick = () => {
    handleMenuClose();
    setIsTagsDialogOpen(true);
  };

  const handleTagsDialogClose = () => {
    setIsTagsDialogOpen(false);
  };

  const handleSaveTags = async ({ lineItemId, tags: newTags, urgentShipByDate }) => {
    const currentTags = tags || [];
    const currentTagNames = currentTags.map((t) => t.tagName);
    const newTagNames = (newTags || []).map((t) => t.tagName);
    const urgentAdded = newTagNames.includes('URGENT') && !currentTagNames.includes('URGENT');
    const urgentRemoved = !newTagNames.includes('URGENT') && currentTagNames.includes('URGENT');
    // User removed and re-added URGENT in the same session to change the ship by date
    const urgentReAdded =
      Boolean(urgentShipByDate) &&
      newTagNames.includes('URGENT') &&
      currentTagNames.includes('URGENT');

    if (!urgentAdded && !urgentRemoved && !urgentReAdded) {
      // No URGENT change — use original path
      return dispatch(upsertLineItemTags({ lineItemId, currentTags, newTags })).then((response) => {
        const updatedTags = first(response?.updatedLineItemTags)?.tags;
        dispatch(
          workQueueActions.updateLineItemTags({
            preset: WorkQueues.PRESCRIPTION_REVIEW,
            page: currentPage,
            lineItemId,
            tags: updatedTags,
          })
        );
        handleTagsDialogClose();
        return response;
      });
    }

    // URGENT tag was added, removed, or re-added with a new ship by date
    let urgentResult;
    if (urgentAdded || urgentReAdded) {
      urgentResult = await dispatch(
        applyUrgentTag({
          lineItemId,
          mpi: patientId,
          orderId,
          shipByDate: urgentShipByDate,
        })
      );
    } else {
      urgentResult = await dispatch(removeUrgentTag({ lineItemId, mpi: patientId, orderId }));
    }

    // Also handle any concurrent non-URGENT tag changes
    const nonUrgentCurrentTags = currentTags.filter((t) => t.tagName !== 'URGENT');
    const nonUrgentNewTags = (newTags || []).filter((t) => t.tagName !== 'URGENT');
    const nonUrgentCurrentNames = nonUrgentCurrentTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    const nonUrgentNewNames = nonUrgentNewTags
      .map((t) => t.tagName)
      .sort()
      .join(',');
    let updatedTags = urgentResult?.tags;
    if (nonUrgentCurrentNames !== nonUrgentNewNames) {
      const tagResponse = await dispatch(
        upsertLineItemTags({
          lineItemId,
          currentTags: nonUrgentCurrentTags,
          newTags: nonUrgentNewTags,
        })
      );
      updatedTags = first(tagResponse?.updatedLineItemTags)?.tags ?? updatedTags;
    }

    dispatch(
      workQueueActions.updateLineItemTags({
        preset: WorkQueues.PRESCRIPTION_REVIEW,
        page: currentPage,
        lineItemId,
        tags: updatedTags,
      })
    );
    handleTagsDialogClose();
    dispatch(filterPrescriptionsNeedsReview({ filter }));
    return { updatedLineItemTags: [{ lineItemId, tags: updatedTags }] };
  };

  if (!prescription) {
    return null;
  }

  return (
    <>
      <InfoDialog />
      <IconButton
        id={`PrescriptionReview-${prescription.rxNumber}-Options`}
        aria-label={`Review Options ${prescription.rxNumber}`}
        onClick={handleMenuOpen}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        id={`Prescription-${prescriptionId}options-menu`}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {isDuplicationReview && <MenuItem onClick={handleComparisonModalView}>View</MenuItem>}
        <MenuItem onClick={openResolveModal}>Resolve</MenuItem>
        {hasDrugIdentity && !isEmpty(sigProfile) && !isFunction(sigProfile) && !isRefillOrder && (
          <MenuItem onClick={() => setOpenChangeReasonDialog(true)}>Change Reason</MenuItem>
        )}
        <MenuItem onClick={handleManageTagsClick}>Manage Tags</MenuItem>
        <ChangeReviewReasonDialog
          open={openChangeReasonDialog}
          onClose={() => setOpenChangeReasonDialog(false)}
          patientId={patientId}
          lineItem={lineItem}
          prescriptionId={prescriptionId}
          orderId={orderId}
          drugName={drugName}
          strength={strength}
          form={form}
          drugUnit={drugUnit}
          firstName={firstName}
          lastName={lastName}
          dob={dob}
          email={email}
          gender={gender}
        />
      </Menu>

      {lineItem && isTagsDialogOpen && (
        <ManageTagsDialog
          open={isTagsDialogOpen}
          onClose={handleTagsDialogClose}
          mpi={patientId}
          orderId={orderId}
          lineItemId={lineItem}
          existingTags={tags || []}
          onSave={handleSaveTags}
          patientDetails={{ patientName }}
        />
      )}

      {resolveModal && isDuplicationReview && (
        <Grid>
          {comparisonView ? (
            <PrescriptionComparisonModel
              open={comparisonView}
              onClose={handleComparisonModalView}
              prescriptionId={prescriptionId}
            />
          ) : (
            <ResolveDuplicatePrescriptionModal
              open={resolveModal}
              onClose={handleResolveDuplicateModal}
              orderId={prescription.orderId}
              prescriptionId={prescriptionId}
            />
          )}
        </Grid>
      )}

      {resolveModal && !isDuplicationReview && !isMultiRxReview && (
        <Dialog open={resolveModal}>
          <DialogContent>
            <Grid container sx={{ px: 8 }} spacing={3}>
              <Grid item xs={12} md={8}>
                <PrescriptionReviewPayloadProvider>
                  <PatientPrescriptionDialog
                    mpi={patientId}
                    open={resolveModal}
                    reviewType={status}
                    contextId={prescriptionId}
                    closeDialog={closeResolveModal}
                    isPrescriptionReview
                    isManualReview={isManualReview}
                  />
                </PrescriptionReviewPayloadProvider>
              </Grid>
            </Grid>

            <Grid className='FooterMessage' sx={{ display: 'none' }}>
              <Typography>This is printed copy and not the original</Typography>
            </Grid>
          </DialogContent>
        </Dialog>
      )}

      {resolveModal && isMultiRxReview && (
        <MultiRxReviewDialog
          open={resolveModal}
          onClose={closeResolveModal}
          prescriptionId={prescriptionId}
          fillType={fillType}
          isManualReview={isManualReview}
        />
      )}
    </>
  );
};

export default PrescriptionReviewOptions;

PrescriptionReviewOptions.propTypes = {
  prescriptionId: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  patientId: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  dob: PropTypes.string,
  email: PropTypes.string,
  gender: PropTypes.string,
  orderId: PropTypes.string,
  lineItem: PropTypes.string,
  drugName: PropTypes.string,
  strength: PropTypes.string,
  fillType: PropTypes.string,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      tagName: PropTypes.string.isRequired,
      expiry: PropTypes.string,
      createdAt: PropTypes.string,
    })
  ),
  patientName: PropTypes.string,
  isManualReview: PropTypes.bool,
};

PrescriptionReviewOptions.defaultProps = {
  patientId: '',
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  dob: PropTypes.string,
  email: PropTypes.string,
  gender: PropTypes.string,
  orderId: PropTypes.string,
  lineItem: PropTypes.string,
  drugName: PropTypes.string,
  strength: PropTypes.string,
  fillType: PropTypes.string,
  tags: [],
  patientName: '',
  isManualReview: false,
};
